/******************************************************************************************
 *	Anniversary Dates Utility
 *******************************************************************************************
 *
 * Utility service for the generation of anniversary dates for choosing an extended term.
 *
 */
(function() {
  

  angular
    .module('rcServices.anniversaryDates', [])
    // 13 – 36 months duration for Commercial customers
    // 13 – 50 months duration for Government & Education customers
    .constant('DEFAULT_ANNIVERSARY_MONTHS_AWAY', 13)
    .constant('SEGMENT_MAX_ANNIVERSARY_MONTHS', {
      COMMERCIAL: 36,
      EDUCATION: 50,
      GOVERNMENT: 50
    })
    .service(
      'AnniversaryDatesUtility',
      /* @ngInject */
      function(
        _,
        DEFAULT_ANNIVERSARY_MONTHS_AWAY,
        feature,
        moment,
        SEGMENT_MAX_ANNIVERSARY_MONTHS
      ) {
        /**
         * @description Method to calculate a date the provided number of months from today. The date returned will be for
         *   that day X months away, at 12:01am Pacific Time.
         *
         * @param {Number} monthsAway the number of months away you wish the returned moment to be. For example,
         *   providing '12' would get the date 12 months from today.
         *
         * @returns {Moment} the moment object representing the date the specified number of months away, at 12:01am Pacific Time
         */
        this.getMomentMonthsAway = function(monthsAway, daysToSubtract = 0) {
          return moment()
            .add(monthsAway, 'months') // today + specified number of months
            .tz('America/Los_Angeles', true)
            .subtract(daysToSubtract, 'days')
            .startOf('day')
            .minute(1); // 00:01 Pacific Time
        };

        /**
         * @description Method to get the default anniversary date, based on the default number of months away from today
         *   that an extended term anniversary date must be.
         *
         * @returns {Date} the default anniversary date
         */
        this.getDefaultAnniversaryDate = function() {
          const defaultMoment = this.getMomentMonthsAway(DEFAULT_ANNIVERSARY_MONTHS_AWAY);
          if (feature.isEnabled('temp_extended_thirteen_months_plus_a_day')) {
            defaultMoment.add(1, 'day'); // make the default anniversary be 13 months plus one day away
          }
          // February 29 is not a valid anniversary date
          if (defaultMoment.month() === 1 && defaultMoment.date() === 29) {
            // move to February 28th
            defaultMoment.date(28);
          }
          return new Date(defaultMoment.format());
        };

        /**
         * @description Method to get the maximum anniversary date for the given segment. This is currently 36 months for
         *   Commercial, and 50 for Education and Government.
         *
         * @param {String} segment the customer segment to get the max date for
         *
         * @returns {Date} the maximum anniversary date
         */
        this.getMaxAnniversaryDateForSegment = function(segment) {
          let maxMonths = SEGMENT_MAX_ANNIVERSARY_MONTHS[segment];
          // If no segment (or an invalid segment) was provided, default to COMMERCIAL
          if (!_.isNumber(maxMonths)) {
            maxMonths = SEGMENT_MAX_ANNIVERSARY_MONTHS.COMMERCIAL;
          }
          return new Date(this.getMomentMonthsAway(maxMonths).format());
        };

        /**
         * @description Method to get a list of months that a valid anniversary date could be in.
         *
         * @param {Date} maxDate the customer segment to get the max date for
         *
         * @returns {Array} a list of objects, each representing a month. It has on it the Date object, timestamp, and year
         */
        this.getAnniversaryDateMonths = function(maxDate, defaultCSegAnniversaryDate = '') {
          const months = [];
          const defaultAnniversaryDate = defaultCSegAnniversaryDate 
            ? defaultCSegAnniversaryDate 
            : this.getDefaultAnniversaryDate();

          // We want to show the month that contains the max date, but not the one after. Get the date that is
          // earliest in the next month (midnight on the 1st of the next month), and don't show months with values
          // greater than its.
          const maxDateNextMonth = moment(maxDate)
            .add(1, 'months')
            .startOf('month');

          // Since the values calculated with 'm' will be passed to the server as anniversary dates, we want
          // to make sure we get them consistently in Pacific Time (whether PST or PDT as appropriate).
          // Using moment to add a month will take care of month math when the current date is greater
          // than 28 (meaning it won't skip February) or is 31 (it won't skip months with 30 days).
          // Moment also maintains the 12:01am Pacific Time time value regardless of DST being active or not.
          for (
            var m = moment.tz(defaultAnniversaryDate.getTime(), 'America/Los_Angeles');
            m.valueOf() < maxDateNextMonth.valueOf();
            m.add(1, 'month')
          ) {
            const month = new Date(Math.min(maxDate.getTime(), m.valueOf()));
            months.push({
              date: month,
              timestamp: month.getTime(),
              year: month.getFullYear()
            });
          }
          return months;
        };

        this.getMomentMonthsAwayWithFormat = function (months, subtractDays) {
          return new Date(this.getMomentMonthsAway(months, subtractDays).format());
        };

        /**
         * @description Method to get a list of dates in a single month that are valid anniversary dates
         *
         * @param {Object} options the selected anniversary date ($scope.anniversaryData)
         * @param {Number} options.anniversaryMonthTimestamp timestamp for the selected anniversary month Date
         * @param {Date} options.maximumAnniversaryDate Date object representing the maximum anniversary date
         * @param {Date} options.minimumAnniversaryDate Date object representing the minimum anniversary date
         *
         * @returns {Array} a list of objects, each representing a month. It has on it the Date object, timestamp, and year
         */
        this.getAnniversaryDaysForMonth = function(options) {
          var selectedMonth = new Date(options.anniversaryMonthTimestamp).getMonth();
          const anniversaryDays = [];
          var momentDay = moment.tz(options.anniversaryMonthTimestamp, 'America/Los_Angeles');
          for (
            momentDay.date(1);
            momentDay.valueOf() <= options.maximumAnniversaryDate &&
            momentDay.month() === selectedMonth;
            momentDay.add(1, 'day')
          ) {
            // Filter out February 29, as it's not a valid anniversary date
            if (
              momentDay.valueOf() < options.minimumAnniversaryDate ||
              (momentDay.month() === 1 && momentDay.date() === 29)
            ) {
              continue;
            }
            anniversaryDays.push(new Date(momentDay.valueOf()));
          }
          return anniversaryDays;
        };
      }
    );
})();
